
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { generatePuzzle } from './utils/sudokuLogic';
import { GameStatus, GameState, Joker, TileType, Difficulty } from './types';
import { useSound } from './components/SoundManager';
import { INITIAL_FOCUS, MAX_JOKERS, TOTAL_STAGES, STAGE_CONFIGS, STARTER_JOKERS, RARITY_COLORS } from './constants';
import SudokuGrid from './components/SudokuGrid';
import Keypad from './components/Keypad';
import HUD from './components/HUD';
import JokerInspector from './components/JokerInspector';

const LottiePlayer = 'lottie-player' as any;

interface FloatingScore {
  id: number;
  val: string;
  x: number;
  y: number;
  color: string;
}

const App: React.FC = () => {
  const [status, setStatus] = useState<GameStatus>('MENU');
  const [selectedDifficulty, setSelectedDifficulty] = useState<Difficulty>('EASY');
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [lastMoveCorrect, setLastMoveCorrect] = useState<boolean | null>(null);
  const [shake, setShake] = useState(false);
  const [inspectingJoker, setInspectingJoker] = useState<Joker | null>(null);
  const [abilityFlash, setAbilityFlash] = useState<{ color: string, id: string | null }>({ color: '', id: null });
  const [floatingScores, setFloatingScores] = useState<FloatingScore[]>([]);
  const { play } = useSound();

  const addFloatingScore = useCallback((val: string, color: string = 'text-white', duration: number = 1500) => {
    const id = Date.now() + Math.random();
    const x = gameState?.selectedCell?.[1] ?? 4;
    const y = (gameState?.selectedCell?.[0] ?? 4) - 0.5;
    
    setFloatingScores(prev => [...prev, { id, val, x, y, color }]);
    setTimeout(() => {
      setFloatingScores(prev => prev.filter(s => s.id !== id));
    }, duration);
  }, [gameState]);

  const createInitialGrid = (difficulty: Difficulty, stage: number) => {
    const { puzzle, solution } = generatePuzzle(difficulty, stage);
    const specialTileCount = Math.min(6, stage + 1);
    const specialPool: TileType[] = stage <= 2 ? ['GOLD', 'GOLD', 'SHIELD', 'VOLATILE'] : ['GOLD', 'SHIELD', 'VOLATILE', 'VOLATILE', 'DEAD'];
    
    const emptyCells: [number, number][] = [];
    puzzle.forEach((row, ri) => row.forEach((cell, ci) => { if (cell === null) emptyCells.push([ri, ci]); }));
    
    const grid = puzzle.map((row) => row.map((val) => ({ 
      value: val, initial: val !== null, notes: [], valid: true, type: 'NORMAL' as TileType 
    })));

    let placed = 0;
    emptyCells.sort(() => Math.random() - 0.5);
    for (const [r, c] of emptyCells) {
      if (placed >= specialTileCount) break;
      const neighbors = [[-1,0],[1,0],[0,-1],[0,1]];
      if (!neighbors.some(([dr, dc]) => {
        const nr = r + dr, nc = c + dc;
        return nr >= 0 && nr < 9 && nc >= 0 && nc < 9 && grid[nr][nc].type !== 'NORMAL';
      }) || Math.random() < 0.3) {
        grid[r][c].type = specialPool[Math.floor(Math.random() * specialPool.length)];
        placed++;
      }
    }
    return { grid, solution };
  };

  const startNewGame = useCallback(() => {
    const config = STAGE_CONFIGS[selectedDifficulty][0];
    const { grid, solution } = createInitialGrid(config.diff, 1);
    const startingFocus = selectedDifficulty === 'EASY' ? INITIAL_FOCUS + 2 : INITIAL_FOCUS;
    
    setGameState({
      grid, solution, focus: startingFocus, maxFocus: startingFocus,
      stress: 0, shield: 0, score: 0, mult: 1.0, money: 8, jokers: [], selectedCell: null,
      targetScore: config.target, round: 1, difficulty: selectedDifficulty, movesInRound: 0, streak: 0, momentumBonus: 0,
      freeMistakeUsed: false, emergencyFocusUsed: false, ruleBendingActive: false, lastSacrificeMove: -10,
      stats: { maxStreak: 0, totalMistakes: 0, totalScore: 0, stagesCleared: 0, mvpJoker: 'None' },
      noteMode: false, history: []
    });
    setStatus('PLAYING');
    play('select');
  }, [selectedDifficulty, play]);

  const undo = useCallback(() => {
    setGameState(prev => {
      if (!prev || prev.history.length === 0) return prev;
      const lastGrid = prev.history[prev.history.length - 1];
      return { ...prev, grid: lastGrid, history: prev.history.slice(0, -1) };
    });
    play('select');
  }, [play]);

  const triggerActiveJoker = useCallback((jokerId: string) => {
    if (!gameState || status !== 'PLAYING') return;
    const joker = gameState.jokers.find(j => j.id === jokerId);
    if (!joker || joker.type !== 'ACTIVE' || (joker.currentUses !== undefined && joker.currentUses <= 0)) return;

    const flashColor = jokerId === 'j_9' ? 'rgba(255, 0, 64, 0.4)' : 'rgba(0, 210, 255, 0.4)';
    setAbilityFlash({ color: flashColor, id: jokerId });
    setTimeout(() => setAbilityFlash({ color: '', id: null }), 600);

    if (jokerId === 'j_9') {
      const [r, c] = gameState.selectedCell || [-1, -1];
      if (r === -1) return;
      const cell = gameState.grid[r][c];
      if (cell.initial || cell.value === null || !cell.valid) return;
      
      setGameState(prev => {
        if (!prev) return null;
        const newGrid = prev.grid.map(row => [...row]);
        newGrid[r][c] = { ...newGrid[r][c], value: null, valid: true };
        addFloatingScore("+250", "text-red-500", 1200);
        return {
          ...prev, grid: newGrid, focus: Math.max(0, prev.focus - 1),
          score: prev.score + 250, mult: prev.mult + 1.0, streak: 0,
          lastSacrificeMove: prev.movesInRound,
          jokers: prev.jokers.map(j => j.id === jokerId ? { ...j, currentUses: (j.currentUses || 1) - 1 } : j)
        };
      });
      play('loss');
    } else if (jokerId === 'j_6') {
      setGameState(prev => prev ? ({
        ...prev, ruleBendingActive: true,
        jokers: prev.jokers.map(j => j.id === jokerId ? { ...j, currentUses: 0 } : j)
      }) : null);
      play('place');
    }
    setInspectingJoker(null);
  }, [gameState, status, play, addFloatingScore]);

  const handleInput = useCallback((num: number) => {
    if (!gameState || !gameState.selectedCell || status !== 'PLAYING') return;
    const [r, c] = gameState.selectedCell;
    if (gameState.grid[r][c].initial) return;

    if (gameState.noteMode) {
      setGameState(prev => {
        if (!prev) return null;
        const newGrid = prev.grid.map(row => [...row]);
        const notes = newGrid[r][c].notes;
        newGrid[r][c] = { ...newGrid[r][c], notes: notes.includes(num) ? notes.filter(n => n !== num) : [...notes, num].sort() };
        return { ...prev, grid: newGrid };
      });
      play('select');
      return;
    }

    const isCorrect = gameState.solution[r][c] === num;
    const bending = gameState.ruleBendingActive;

    setGameState(prev => {
      if (!prev) return null;
      const currentGridSnapshot = prev.grid.map(row => row.map(cell => ({ ...cell })));
      const newGrid = prev.grid.map(row => [...row]);
      if (newGrid[r][c].value === num && newGrid[r][c].valid) return prev;

      const placementValid = isCorrect || bending;
      newGrid[r][c] = { ...newGrid[r][c], value: num, valid: placementValid, notes: [] };

      let nextState: GameState = { 
        ...prev, grid: newGrid, ruleBendingActive: false, 
        movesInRound: prev.movesInRound + 1,
        history: [...prev.history, currentGridSnapshot].slice(-10) 
      };

      if (!placementValid) {
        setShake(true); setTimeout(() => setShake(false), 500);
        setLastMoveCorrect(false); setTimeout(() => setLastMoveCorrect(null), 500);
        nextState.stats.totalMistakes += 1;
        const hasSecondChance = nextState.jokers.some(j => j.id === 'j_1') && !nextState.freeMistakeUsed;
        const hasGlassBrain = nextState.jokers.some(j => j.id === 'j_3');
        const damage = (hasGlassBrain ? 2 : 1) + (newGrid[r][c].type === 'VOLATILE' ? 1 : 0);
        
        if (nextState.shield > 0) nextState.shield -= 1;
        else if (hasSecondChance) nextState.freeMistakeUsed = true;
        else { nextState.focus -= damage; nextState.stress += 1; }

        if (nextState.jokers.some(j => j.id === 'j_5')) {
          const empty: [number, number][] = [];
          nextState.grid.forEach((row, ri) => row.forEach((cell, ci) => { if (cell.value === null) empty.push([ri, ci]); }));
          if (empty.length > 0) {
            const [er, ec] = empty[Math.floor(Math.random() * empty.length)];
            nextState.grid[er][ec] = { ...nextState.grid[er][ec], value: nextState.solution[er][ec], valid: true };
          }
        }
        nextState.streak = 0; nextState.mult = 1.0;
        play('error');
      } else {
        setLastMoveCorrect(true); setTimeout(() => setLastMoveCorrect(null), 500);
        nextState.streak += 1;
        nextState.stats.maxStreak = Math.max(nextState.stats.maxStreak, nextState.streak);
        
        let base = (newGrid[r][c].type === 'DEAD') ? 0 : 25;
        if (newGrid[r][c].type === 'GOLD') base += 100;
        if (newGrid[r][c].type === 'VOLATILE') base += 150;
        if (nextState.jokers.some(j => j.id === 'j_2') && nextState.streak >= 3) base *= 2;
        if (nextState.jokers.some(j => j.id === 'j_10') && nextState.streak % 6 === 0) nextState.shield += 1;
        if (newGrid[r][c].type === 'SHIELD') nextState.shield += 1;

        let combo = nextState.streak >= 12 ? 4 : nextState.streak >= 8 ? 2.5 : nextState.streak >= 5 ? 2 : nextState.streak >= 3 ? 1.5 : 1;
        const gain = Math.floor(base * nextState.mult * combo);
        nextState.score += gain; nextState.stats.totalScore += gain;
        addFloatingScore(`+${gain}`, nextState.streak >= 8 ? 'text-orange-500' : nextState.streak >= 3 ? 'text-yellow-400' : 'text-blue-400', 1000);
        play('place');

        let structures = 0;
        let structureNames: string[] = [];
        if (newGrid[r].every(c => c.value !== null && c.valid)) { structures++; structureNames.push("ROW!"); }
        if (newGrid.every(row => row[c].value !== null && row[c].valid)) { structures++; structureNames.push("COL!"); }
        const sr = r - r % 3, sc = c - c % 3;
        let boxC = true;
        for(let i=0; i<3; i++) for(let j=0; j<3; j++) if(newGrid[sr+i][sc+j].value === null || !newGrid[sr+i][sc+j].valid) boxC = false;
        if(boxC) { structures++; structureNames.push("BOX!"); }

        if (structures > 0) {
          const bonus = (structures === 1 ? 250 : structures === 2 ? 600 : 1500);
          nextState.score += bonus;
          addFloatingScore(`${structureNames.join(" ")} +${bonus}`, 'text-purple-400', 2000);
          if (boxC && nextState.jokers.some(j => j.id === 'j_4')) nextState.focus = Math.min(nextState.maxFocus, nextState.focus + 1);
          play('rowComplete');
        }
      }

      if (nextState.focus <= 0) {
        setTimeout(() => setStatus('DEFEAT'), 800);
      } else if (nextState.score >= nextState.targetScore) {
        nextState.stats.stagesCleared += 1;
        nextState.money += 7;
        setTimeout(() => { setStatus('SHOP'); play('win'); }, 1000);
      }
      return nextState;
    });
  }, [gameState, status, play, addFloatingScore]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (status !== 'PLAYING') return;
      if (e.key >= '1' && e.key <= '9') handleInput(parseInt(e.key));
      if (e.key.toLowerCase() === 'n') setGameState(p => p ? ({ ...p, noteMode: !p.noteMode }) : null);
      if (e.key.toLowerCase() === 'z' && e.ctrlKey) undo();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [status, handleInput, undo]);

  if (status === 'MENU') return (
    <div className="flex flex-col items-center justify-center h-screen bg-[#0a0a0a] relative overflow-hidden p-4">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
        <LottiePlayer src="https://assets8.lottiefiles.com/packages/lf20_m469onob.json" background="transparent" speed="0.3" loop autoplay style={{width: '100%', height: '100%'}} />
      </div>
      <div className="text-center z-10 p-6 md:p-12 balatro-gradient rounded-[2rem] md:rounded-[3rem] border-4 border-[#ff0040]/20 backdrop-blur-sm animate-in fade-in zoom-in duration-700 max-w-full">
        <h1 className="text-6xl md:text-[10rem] font-bold tracking-tighter mb-4 glow-text-red italic transform -rotate-3 select-none leading-none">SUDOKTRO</h1>
        <p className="text-gray-400 mb-8 tracking-[0.2em] md:tracking-[0.5em] uppercase font-vt323 text-lg md:text-xl">ARCADE RUN ENGINE v1.2</p>
        
        <div className="flex flex-wrap gap-2 md:gap-4 justify-center mb-10">
          {(['EASY', 'MEDIUM', 'HARD'] as Difficulty[]).map(d => (
            <button 
              key={d} 
              onClick={() => { setSelectedDifficulty(d); play('select'); }}
              className={`px-6 md:px-8 py-2 md:py-3 rounded-full font-black text-[10px] md:text-xs tracking-widest border transition-all ${selectedDifficulty === d ? 'bg-white text-black border-white shadow-[0_0_20px_rgba(255,255,255,0.4)]' : 'bg-transparent text-gray-500 border-gray-800 hover:border-white/20'}`}
            >
              {d}
            </button>
          ))}
        </div>

        <button onClick={startNewGame} className="bg-[#ff0040] text-white px-10 md:px-16 py-4 md:py-6 rounded-full font-bold text-xl md:text-3xl hover:scale-110 shadow-[0_0_50px_rgba(255,0,64,0.4)] transition-all glitch-hover">START RUN</button>
      </div>
    </div>
  );

  if (status === 'VICTORY' || status === 'DEFEAT') return (
    <div className="flex flex-col items-center justify-center h-screen bg-[#050505] p-6">
      <div className="max-w-2xl w-full balatro-gradient p-8 md:p-12 rounded-[2rem] md:rounded-[3rem] border-4 border-white/10 text-center animate-in zoom-in duration-700">
        <h2 className={`text-5xl md:text-8xl font-black italic mb-4 ${status === 'VICTORY' ? 'text-yellow-400 glow-text-blue' : 'text-red-600 glow-text-red'}`}>
          {status === 'VICTORY' ? 'YOU WIN!' : 'RUN FAILED'}
        </h2>
        <div className="w-full h-px bg-white/5 my-6 md:my-8"></div>
        <div className="grid grid-cols-2 gap-4 md:gap-8 text-left mb-10 md:track-12">
          <div><div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Total Score</div><div className="text-xl md:text-3xl font-black tabular-nums">{gameState?.stats.totalScore.toLocaleString()}</div></div>
          <div><div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Max Streak</div><div className="text-xl md:text-3xl font-black tabular-nums">x{gameState?.stats.maxStreak}</div></div>
          <div><div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Stages Cleared</div><div className="text-xl md:text-3xl font-black tabular-nums">{gameState?.stats.stagesCleared} / 5</div></div>
          <div><div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Total Mistakes</div><div className="text-xl md:text-3xl font-black tabular-nums">{gameState?.stats.totalMistakes}</div></div>
        </div>
        <button onClick={() => setStatus('MENU')} className="bg-white text-black px-10 py-3 rounded-full font-bold text-xl md:text-2xl hover:scale-105 transition-all">MAIN MENU</button>
      </div>
    </div>
  );

  if (status === 'SHOP') return (
    <div className="flex flex-col items-center justify-center h-screen bg-[#070707] p-4 overflow-hidden">
      <div className="max-w-6xl w-full balatro-gradient p-6 md:p-10 rounded-[2rem] md:rounded-[3rem] border-2 border-yellow-500/10 shadow-2xl animate-in zoom-in-95 duration-500 max-h-full flex flex-col">
        <div className="flex justify-between items-end mb-6 border-b border-white/5 pb-4">
          <div><h2 className="text-[10px] uppercase tracking-[0.5em] text-yellow-500/50">Restock</h2><h2 className="text-4xl md:text-6xl font-bold italic text-yellow-500">THE SHOP</h2></div>
          <div className="text-4xl md:text-6xl font-mono text-green-400 font-bold tabular-nums">${gameState?.money}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 flex-1 overflow-y-auto custom-scrollbar pr-2">
          {STARTER_JOKERS.sort(() => 0.5 - Math.random()).slice(0, 3).map(j => {
            const owned = gameState?.jokers.some(oj => oj.id === j.id);
            const canAfford = (gameState?.money || 0) >= j.cost;
            return (
              <div key={j.id} className={`p-4 md:p-6 rounded-2xl border transition-all flex flex-col justify-between ${owned ? 'border-gray-800 opacity-30 grayscale' : 'border-white/5 hover:border-yellow-500/30 bg-white/[0.02]'}`}>
                <div><div className={`text-[10px] font-black uppercase mb-2 ${j.rarity === 'Rare' ? 'text-purple-400' : 'text-gray-500'}`}>{j.rarity}</div><div className="text-xl md:text-2xl font-bold text-white mb-2">{j.name}</div><div className="text-[10px] md:text-xs text-gray-400 mb-6 italic h-12 leading-relaxed">{j.description}</div></div>
                <button disabled={owned || !canAfford || (gameState?.jokers.length || 0) >= MAX_JOKERS} onClick={() => {
                   setGameState(p => p ? ({ ...p, money: p.money - j.cost, jokers: [...p.jokers, { ...j, currentUses: j.usesPerPuzzle }] }) : null); play('place');
                }} className={`w-full py-3 md:py-4 rounded-xl font-bold text-black text-sm md:text-base ${canAfford && !owned ? 'bg-yellow-500 hover:bg-yellow-400' : 'bg-gray-800'}`}>{owned ? 'OWNED' : `$ ${j.cost}`}</button>
              </div>
            );
          })}
        </div>
        <button onClick={() => {
          if (!gameState) return;
          const stageIdx = gameState.round;
          if (stageIdx >= TOTAL_STAGES) setStatus('VICTORY');
          else {
            const config = STAGE_CONFIGS[gameState.difficulty][stageIdx];
            const { grid, solution } = createInitialGrid(config.diff, stageIdx + 1);
            
            // Rebalancing boss focus: Easy difficulty boss stages won't reduce max focus as drastically
            const nextMaxFocus = (gameState.difficulty === 'EASY' && stageIdx === 4) 
              ? Math.max(gameState.maxFocus, 5) 
              : (stageIdx === 4 ? 3 : gameState.maxFocus);
            
            setGameState(p => p ? ({ 
              ...p, grid, solution, round: stageIdx + 1, targetScore: config.target, 
              difficulty: config.diff, focus: Math.min(nextMaxFocus, p.focus + 1), 
              maxFocus: nextMaxFocus, history: [] 
            }) : null);
            setStatus('PLAYING'); play('select');
          }
        }} className="bg-white text-black px-12 md:px-24 py-4 md:py-6 rounded-full font-bold text-xl md:text-3xl hover:scale-105 transition-all self-center mt-auto">ENTER NEXT BLIND</button>
      </div>
    </div>
  );

  return (
    <div className={`flex flex-col lg:flex-row h-screen max-h-screen p-2 md:p-6 gap-2 md:gap-6 items-center justify-center bg-[#050505] relative font-mono overflow-hidden ${shake ? 'shake' : ''}`}>
      <div className={`ability-flash-overlay ${abilityFlash.id ? 'flash-active' : ''}`} style={{ background: `radial-gradient(circle, ${abilityFlash.color} 0%, transparent 70%)` }} />

      {floatingScores.map(s => (
        <div key={s.id} className={`score-popup ${s.color} text-[10px] md:text-sm max-w-[200px] text-center leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]`} style={{ left: `calc(50% + ${(s.x - 4) * (window.innerWidth < 768 ? 32 : 56)}px)`, top: `calc(50% + ${(s.y - 4) * (window.innerWidth < 768 ? 32 : 56)}px)` }}>
          {s.val}
        </div>
      ))}

      {inspectingJoker && (
        <JokerInspector 
          joker={inspectingJoker} 
          onClose={() => setInspectingJoker(null)} 
          onActivate={triggerActiveJoker} 
          isAbilityFlashing={abilityFlash.id === inspectingJoker.id} 
        />
      )}

      {/* HUD Left */}
      {gameState && (
        <HUD gameState={gameState} onJokerClick={setInspectingJoker} />
      )}

      {/* Grid Center */}
      <div className="relative group z-10 flex flex-col gap-2 md:gap-4 flex-grow-0 flex-shrink min-h-0 items-center">
        {/* Mobile Header */}
        <div className="lg:hidden flex w-full justify-between items-center px-4 py-1 balatro-gradient rounded-xl border border-white/5 mb-2">
            <div className="flex items-center gap-2">
                <div className="text-[10px] font-black text-red-500">{gameState?.focus}/{gameState?.maxFocus}</div>
                <div className="w-16 h-1 bg-white/10 rounded-full"><div className="h-full bg-red-500 rounded-full" style={{width: `${((gameState?.focus ?? 0) / (gameState?.maxFocus ?? 5)) * 100}%`}}></div></div>
            </div>
            <div className="text-xs font-black text-blue-400 tabular-nums">{gameState?.score.toLocaleString()} <span className="text-[8px] opacity-40">/ {gameState?.targetScore.toLocaleString()}</span></div>
        </div>

        {gameState && (
          <SudokuGrid 
            gameState={gameState} 
            onCellClick={(r, c) => {
              setGameState(p => p ? ({...p, selectedCell: [r,c]}) : null);
              play('select');
            }} 
            lastMoveCorrect={lastMoveCorrect}
          />
        )}
        
        {/* Number Selection Bar (MOBILE ONLY) */}
        <div className="lg:hidden w-full">
          {gameState && <Keypad gameState={gameState} onInput={handleInput} onToggleNote={() => setGameState(p => p ? ({ ...p, noteMode: !p.noteMode }) : null)} onUndo={undo} isMobile={true} />}
        </div>
      </div>

      {/* Stats Right (KEYPAD) */}
      <div className="hidden lg:flex flex-col w-64 gap-3 z-10 animate-in fade-in slide-in-from-right duration-500 flex-shrink-0">
        <div className="balatro-gradient p-3 rounded-xl border border-white/5 text-center shadow-lg">
          <div className="text-[10px] text-gray-500 font-bold uppercase mb-2">Stage Anomaly</div>
          <div className="text-[9px] text-blue-400 font-black uppercase leading-tight">
            {STAGE_CONFIGS[gameState?.difficulty || 'EASY'][(gameState?.round || 1) - 1].twist}
          </div>
        </div>

        {gameState && <Keypad gameState={gameState} onInput={handleInput} onToggleNote={() => setGameState(p => p ? ({ ...p, noteMode: !p.noteMode }) : null)} onUndo={undo} isMobile={false} />}

        <div className="flex-1 min-h-0"></div>
        
        <button onClick={() => confirm('Abort session?') && setStatus('MENU')} className="w-full py-3 text-[10px] text-red-600/40 hover:text-red-500 uppercase tracking-widest font-black border border-red-600/10 rounded-xl hover:bg-red-900/5 transition-all">ABORT RUN</button>
      </div>
    </div>
  );
};

export default App;
